/* The one way a settings page lists things — apps, servers, files, versions — and the frame of
   every block in an answer (code, a diagram, a table): a white card in a grey tray (the settings
   atoms' `RowList` look). The tray's strip above the card says what it holds and carries its one
   action; the card holds one row per item, or its empty state. Rows lead with a 32px tile, so
   logos, icons and letters line up from list to list. A card that folds (a turn's tool calls)
   makes its strip one button with a chevron. */
import React from "react";
import { ChevronDown } from "lucide-react";

import Fold from "@/shared/components/Fold";
import { cn } from "@/shared/lib/cn";

import type { LucideIcon } from "lucide-react";

/** "3 apps": the count in mono, as every number is. */
export function Counted({ n, noun, plural = `${noun}s` }: { n: number; noun: string; plural?: string }) {
  return <><span className="font-mono tabular-nums">{n}</span> {n === 1 ? noun : plural}</>;
}

export function ListCard({ label, action, danger = false, folded = false, onToggle, className = "", children }: {
  /** The header row's text — usually a count. No label and no action, no header row. */
  label?: React.ReactNode;
  /** The header row's one control, at its end: Add, a note, a switch. */
  action?: React.ReactNode;
  /** A card of irreversible actions: its ring says so. */
  danger?: boolean;
  /** With `onToggle`, the card folds under its header row, which is then one button with a
      chevron at its end. */
  folded?: boolean;
  onToggle?: () => void;
  className?: string;
  children?: React.ReactNode;
}) {
  const labelText = `min-w-0 truncate text-[12.5px] font-medium ${danger ? "text-[var(--danger)]" : "text-[var(--text-secondary)]"}`;
  const header = onToggle ? (
    <button
      type="button"
      aria-expanded={!folded}
      onClick={onToggle}
      className="group w-full flex items-center justify-between gap-3 min-h-[40px] pl-3 pr-2.5 pb-1 rounded-t-[14px] text-left outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--hairline-focus)]"
    >
      <div className={labelText}>{label}</div>
      <span className="shrink-0 flex items-center gap-2">
        {action}
        <ChevronDown
          className={cn("w-3.5 h-3.5 text-[var(--text-quaternary)] group-hover:text-[var(--text-secondary)] transition-transform [transition-duration:360ms] [transition-timing-function:var(--easing)]", !folded && "rotate-180")}
          strokeWidth={1.75}
        />
      </span>
    </button>
  ) : (
    <div className="flex items-center justify-between gap-3 min-h-[40px] pl-3 pr-1 pb-1">
      <div className={labelText}>{label}</div>
      {action && <div className="shrink-0 flex items-center gap-2">{action}</div>}
    </div>
  );
  const card = (
    <div className={`rounded-[14px] bg-[var(--surface)] ring-1 ${danger ? "ring-[var(--danger-ring)]" : "ring-[var(--hairline)]"} shadow-lift overflow-hidden divide-y divide-[var(--hairline)]`}>
      {children}
    </div>
  );
  return (
    <div className={`rounded-[18px] p-1 ${danger ? "bg-[var(--danger-bg)]" : "bg-[var(--surface-2)]"} ${className}`}>
      {(label || action) && header}
      {onToggle ? <Fold open={!folded}>{card}</Fold> : card}
    </div>
  );
}

/** The square that leads a row: an icon, a favicon or a letter. Logos that
    draw their own tile (`AppLogo`, `FileTypeIcon`) take its 32px instead. */
export function ListTile({ children, className = "" }: { children?: React.ReactNode; className?: string }) {
  return (
    <span className={`w-8 h-8 shrink-0 rounded-[8px] bg-[var(--surface-2)] flex items-center justify-center text-[var(--text-secondary)] ${className}`}>
      {children}
    </span>
  );
}

/** One item: what leads it, its name and a line under it, then whatever trails —
    chips, a count, and actions that show on hover. Clickable when `onClick` is. */
export function ListRow({ lead, title, desc, muted = false, onClick, rowTitle, children }: {
  lead?: React.ReactNode;
  title: React.ReactNode;
  desc?: React.ReactNode;
  /** Switched off for this agent: the name steps back. */
  muted?: boolean;
  onClick?: () => void;
  /** The row's hover tooltip. */
  rowTitle?: string;
  children?: React.ReactNode;
}) {
  const press = onClick && {
    role: "button",
    tabIndex: 0,
    onClick,
    onKeyDown: (e: React.KeyboardEvent) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } },
  };
  return (
    <div
      {...press}
      title={rowTitle}
      className={`group flex items-center gap-3 px-4 py-3 min-w-0 ${onClick ? "press cursor-pointer hover:bg-[var(--accent-hover)]" : ""}`}
    >
      {lead}
      <div className="min-w-0 flex-1">
        <div className={`text-[13.5px] font-medium tracking-[-0.005em] truncate ${muted ? "text-[var(--text-tertiary)]" : "text-[var(--text-primary)]"}`}>{title}</div>
        {desc && <div className="mt-[1px] text-[12.5px] text-[var(--text-tertiary)] truncate">{desc}</div>}
      </div>
      {children}
    </div>
  );
}

/** What a list shows while it holds nothing — or while its section is off. */
export function ListEmpty({ icon: Icon, title, hint, action }: {
  icon: LucideIcon;
  title: string;
  hint?: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div className="px-6 py-10 flex flex-col items-center text-center fade-in">
      <div className="w-10 h-10 rounded-[12px] bg-[var(--surface-sunk)] ring-1 ring-[var(--hairline)] flex items-center justify-center">
        <Icon className="w-[18px] h-[18px] text-[var(--text-tertiary)]" strokeWidth={1.5} />
      </div>
      <div className="mt-3 text-[14px] font-medium tracking-[-0.005em] text-[var(--text-primary)]">{title}</div>
      {hint && <p className="m-0 mt-1 max-w-[380px] text-[12.5px] leading-[1.55] text-[var(--text-tertiary)]">{hint}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}
